<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Shopping Cart</h1>
            <p class="text-orange-100 text-lg">Review your selected items</p>
          </div>
          <div class="flex space-x-3">
            <router-link 
              to="/customer/catalogue" 
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 text-white"
            >
              Continue Shopping
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
      <p class="mt-2 text-gray-500">Loading your cart...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8">
      <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 class="text-xl font-bold text-gray-900 mb-2">Failed to load cart</h3>
      <p class="text-gray-600 mb-4">{{ error }}</p>
      <button
        @click="loadCart"
        class="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
      >
        Try Again
      </button>
    </div>

    <!-- Cart Content -->
    <div v-else>
      <!-- Empty Cart State -->
      <div v-if="cartItems.length === 0" class="text-center py-8">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
          </svg>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-2">Your cart is empty</h3>
        <p class="text-gray-600 mb-4">Start shopping to add items to your cart</p>
        <router-link 
          to="/customer/catalogue" 
          class="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          Browse Collection
        </router-link>
      </div>

      <!-- Cart Items -->
      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Cart Items List -->
        <div class="lg:col-span-2">
          <div class="space-y-6">
            <div
              v-for="item in cartItems"
              :key="item.id"
              class="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <div class="p-6">
                <div class="flex items-start space-x-4">
                  <!-- Product Image -->
                  <div class="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                    <img 
                      v-if="item.images && item.images.length > 0"
                      :src="getImageUrl(item.images[0])"
                      :alt="item.name"
                      class="w-full h-full object-contain rounded-lg"
                    />
                    <span v-else class="text-gray-600 font-bold text-xl">{{ item.name.charAt(0) }}</span>
                  </div>

                  <!-- Product Info -->
                  <div class="flex-1 min-w-0">
                    <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ item.name }}</h3>
                    <p class="text-gray-600 text-sm mb-2">SKU: {{ item.sku }}</p>
                    <p class="text-gray-500 text-sm mb-4">{{ item.description }}</p>
                    
                    <!-- Quantity Controls -->
                    <div class="flex items-center space-x-4">
                      <div class="flex items-center space-x-2">
                        <button
                          @click="decreaseQuantity(item.id)"
                          :disabled="item.quantity <= 1 || updatingItem === item.id"
                          class="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                          </svg>
                        </button>
                        <span class="w-12 text-center font-semibold text-gray-900">{{ item.quantity }}</span>
                        <button
                          @click="increaseQuantity(item.id)"
                          :disabled="updatingItem === item.id"
                          class="w-8 h-8 bg-orange-500 text-white rounded-lg flex items-center justify-center hover:bg-orange-600 transition-colors"
                        >
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                      
                      <button
                        @click="removeItem(item.id)"
                        :disabled="removingItem === item.id"
                        class="text-red-600 hover:text-red-700 font-medium text-sm transition-colors disabled:opacity-50"
                      >
                        {{ removingItem === item.id ? 'Removing...' : 'Remove' }}
                      </button>
                    </div>
                  </div>

                  <!-- Quantity Info -->
                  <div class="text-right">
                    <p class="text-lg font-semibold text-gray-900">{{ item.quantity }} item(s)</p>
                    <p class="text-gray-500 text-sm">Quantity</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Checkout Section -->
        <div class="lg:col-span-1">
          <div class="bg-white rounded-lg shadow-lg p-6 sticky top-8">
            <h3 class="text-2xl font-semibold text-gray-900 mb-6">Ready to Order?</h3>
            
            <div class="space-y-4 mb-6">
              <div class="flex justify-between text-lg">
                <span class="text-gray-600">Items:</span>
                <span class="font-semibold text-gray-900">{{ totalItems }} item(s)</span>
              </div>
              <p class="text-sm text-gray-600 text-center">
                Place your order and we'll contact you with pricing details.
              </p>
            </div>

            <router-link
              to="/customer/checkout"
              class="w-full bg-orange-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center"
            >
              Place Order
            </router-link>

            <div class="mt-4 text-center">
              <router-link
                to="/customer/catalogue"
                class="text-orange-600 hover:text-orange-700 font-medium transition-colors hover:underline"
              >
                Continue Shopping
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { cartAPI } from '../services/api'

export default {
  name: 'Cart',
  setup() {
    const router = useRouter()
    
    // State
    const cartItems = ref([])
    const cartSummary = ref({
      total_items: 0,
      total_weight: 0,
      total_amount: 0
    })
    const loading = ref(false)
    const error = ref('')
    const updatingItem = ref(null)
    const removingItem = ref(null)

    // Computed properties
    const totalItems = computed(() => {
      return cartItems.value.reduce((total, item) => total + item.quantity, 0)
    })

    // Removed price calculations as rates are set by admin

    // Methods
    const loadCart = async () => {
      loading.value = true
      error.value = ''
      
      try {
        const [items, summary] = await Promise.all([
          cartAPI.getCart(),
          cartAPI.getCartSummary()
        ])
        
        cartItems.value = items
        cartSummary.value = summary
      } catch (err) {
        console.error('Failed to load cart:', err)
        error.value = err.response?.data?.message || 'Failed to load cart'
      } finally {
        loading.value = false
      }
    }

    const increaseQuantity = async (itemId) => {
      const item = cartItems.value.find(item => item.id === itemId)
      if (!item) return

      updatingItem.value = itemId
      try {
        await cartAPI.updateCartItem(itemId, item.quantity + 1)
        await loadCart() // Reload cart to get updated data
      } catch (err) {
        console.error('Failed to update quantity:', err)
      } finally {
        updatingItem.value = null
      }
    }

    const decreaseQuantity = async (itemId) => {
      const item = cartItems.value.find(item => item.id === itemId)
      if (!item || item.quantity <= 1) return

      updatingItem.value = itemId
      try {
        await cartAPI.updateCartItem(itemId, item.quantity - 1)
        await loadCart() // Reload cart to get updated data
      } catch (err) {
        console.error('Failed to update quantity:', err)
      } finally {
        updatingItem.value = null
      }
    }

    const removeItem = async (itemId) => {
      removingItem.value = itemId
      try {
        await cartAPI.removeFromCart(itemId)
        await loadCart() // Reload cart to get updated data
      } catch (err) {
        console.error('Failed to remove item:', err)
      } finally {
        removingItem.value = null
      }
    }

    const proceedToCheckout = () => {
      router.push('/checkout')
    }

    // Removed formatPrice as pricing is handled by admin

    const getImageUrl = (imageName) => {
      // Use a working placeholder service or return a fallback
      if (!imageName) {
        return 'data:image/svg+xml;base64,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'
      }
      
      // If it's a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    // Lifecycle
    onMounted(() => {
      loadCart()
    })

    return {
      cartItems,
      cartSummary,
      loading,
      error,
      updatingItem,
      removingItem,
      totalItems,
      loadCart,
      increaseQuantity,
      decreaseQuantity,
      removeItem,
      proceedToCheckout,
      getImageUrl
    }
  }
}
</script> 